import React, { useState, useEffect, useCallback } from 'react';
import { Button, Switch, Spin, notification, Collapse } from 'antd';
import { useFetch, useSave } from 'store';
import services from '_src/services';

import { SettingsGrid, SettingCard, SettingTitle, SettingDescription, ButtonsRow } from '../styles';

const { Panel } = Collapse;
const laptopConfig = services['laptopLending'];

const formatSettingKey = (key) => {
  if (!key) return '';
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const CATEGORY_LABELS = {
  loan_process: 'Loan Process',
  accessories: 'Accessories',
  asset_tracking: 'Asset Tracking',
  maintenance: 'Maintenance',
  procurement: 'Procurement',
  transfers: 'Transfers'
};

export default function LaptopSettings({ branchId }) {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { get: fetchSettings } = useFetch(laptopConfig.getSettings);
  const { save: updateSettings } = useSave(laptopConfig.updateSettings);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const params = branchId ? { branch_id: branchId } : {};
      const response = await fetchSettings({ params });
      const loadedSettings = response?.settings || response?.[0]?.data?.settings || [];
      setSettings(loadedSettings);
    } catch (error) {
      notification.error({ message: 'Failed to load settings', duration: 5 });
      setSettings([]);
    }
    setLoading(false);
  }, [branchId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleToggle = (settingKey, field, value) => {
    setSettings(prev => prev.map(s => {
      if (s.settingKey === settingKey) {
        return { ...s, [field]: value };
      }
      return s;
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);

    const payload = {
      branch_id: branchId,
      settings: settings.map(s => ({
        setting_key: s.settingKey,
        enabled: s.enabled,
        required: s.required
      }))
    };

    const response = await updateSettings(payload);

    setSaving(false);

    if (response?.success === 1) {
      notification.success({ message: 'Settings saved successfully', duration: 5 });
      setHasChanges(false);
    } else {
      notification.error({
        message: response?.data?.message || 'Failed to save settings',
        duration: 5
      });
    }
  };

  const groupedSettings = settings.reduce((acc, setting) => {
    const category = setting.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(setting);
    return acc;
  }, {});

  const categoryOrder = ['loan_process', 'accessories', 'asset_tracking', 'maintenance', 'procurement', 'transfers'];
  const sortedCategories = Object.keys(groupedSettings).sort((a, b) => {
    const indexA = categoryOrder.indexOf(a);
    const indexB = categoryOrder.indexOf(b);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return (
    <div>
      <Spin spinning={loading}>
        {sortedCategories.length > 0 ? (
          <Collapse defaultActiveKey={['loan_process']} style={{ marginBottom: 20 }}>
            {sortedCategories.map(category => (
              <Panel
                header={CATEGORY_LABELS[category] || formatSettingKey(category)}
                key={category}
              >
                <SettingsGrid>
                  {groupedSettings[category].map((setting, index) => (
                    <SettingCard key={setting.settingKey || index}>
                      <SettingTitle>
                        {formatSettingKey(setting.settingKey)}
                      </SettingTitle>
                      <SettingDescription>
                        {setting.description || ''}
                      </SettingDescription>
                      <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginTop: 10 }}>
                        <div>
                          <span style={{ marginRight: 8 }}>Enabled:</span>
                          <Switch
                            checked={setting.enabled}
                            onChange={checked => handleToggle(setting.settingKey, 'enabled', checked)}
                          />
                        </div>
                        <div>
                          <span style={{ marginRight: 8 }}>Required:</span>
                          <Switch
                            checked={setting.required}
                            onChange={checked => handleToggle(setting.settingKey, 'required', checked)}
                            disabled={!setting.enabled}
                          />
                        </div>
                      </div>
                      {setting.source && (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                          Source: {setting.source}
                        </div>
                      )}
                    </SettingCard>
                  ))}
                </SettingsGrid>
              </Panel>
            ))}
          </Collapse>
        ) : (
          !loading && (
            <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
              No settings found. Settings will appear here once configured.
            </div>
          )
        )}

        {hasChanges && (
          <ButtonsRow style={{ marginTop: 20 }}>
            <Button onClick={loadSettings}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleSave} loading={saving}>
              Save Settings
            </Button>
          </ButtonsRow>
        )}
      </Spin>
    </div>
  );
}
